import React, {
  useState, useEffect, useRef, CSSProperties
} from 'react';
import { Button, Avatar, Popover } from '@barges/react';
import '@barges/scss/lib/avatar.css';
import '@barges/scss/lib/popover.css';
import { TFunction } from 'i18next';
import { SvgIcon, PurchaseSvgIcon } from '@common/sprites';
import cn from 'classnames';
import { Trans } from 'react-i18next';

import { isKA, isPS } from '@common/config/utils';
import { trackToBugsnagApiError } from '@common/config/app_logger';
import { advisorTipsBonusesSubmitApi, getAdvisorBonusApi } from '@common/api/api';
import { analyticsService } from '@common/analytics';
import { ANALYTICS_EVENTS } from '@common/analytics/AnalyticsEvents';
import { usePurchaseFlowRenderProps } from '@common/config/purchaseFlowRenderHelpers';
import classes from './classes.module.scss';
import withPGTranslation from '../../config/withPGTranslation';
import { PurchaseButtonWrapper } from '../purchase_flow';
import { parseAndTrackAnalyticEvents } from '../../actions/analytics';
import { addParamsToAnalitycs } from '../../actions/application';
import { checkIfCanMakeApplePayPayments } from '../../actions/purchase_details';
import { formatLocalCurrency, getCurrencySignForCode, formatLocalCurrencyNumberOnly } from '../../config/util';

export interface ParamsProps {
  bonus_amount: Partial<Advisor.Bonus>;
  payment_amount?: {
    amount: number,
    kind: Payment.KindType,
    id: number,
    gatewayName: string,
    context: object,
    purchaseFees: number
  }
}

export interface MainAdvisorBonusProps {
  t: TFunction;
  user: User.Details;
  loading: boolean;
  skipBtnHandler: (params?: object) => void;
  initialLoadedHandler: () => void;
  forMobileSendBonusBtnOnClickHandler?: (params: ParamsProps) => void;
  onPaymentError?: (error?: string) => void;
  orderId?: number;
  classNames?: string;
  isOpen: boolean;
  clickSource?: string;
  hideSkipButton?: boolean;
  setAdvisorBonusSkipEventProperties?: (data: object) => void;
}

const bonusIconsIds = ['low_bonus', 'medium_bonus', 'high_bonus'];
const popoverStyles: CSSProperties = { padding: 25, width: '343px' };

const AdvisorBonus = (props: MainAdvisorBonusProps) => {
  const {
    t, user, loading, skipBtnHandler, initialLoadedHandler, forMobileSendBonusBtnOnClickHandler, onPaymentError, orderId, classNames, clickSource, hideSkipButton, setAdvisorBonusSkipEventProperties
  } = props;
  const { renderIcon, renderText } = usePurchaseFlowRenderProps();
  const currentRef = useRef(null);
  const [selectedBonus, setSelectedBonus] = useState<Partial<Advisor.Bonus>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [fetchData, setFetchData] = useState(null);

  const [purchaseButtonProcessing, setPurchaseButtonProcessing] = useState(false);
  const advisor = fetchData?.advisor;
  const bonuses: Advisor.BonusList = fetchData?.advisorBonus || { bonusAmounts: [], currency: 'USD', fxRate: 1 };
  const skeletonLoading = loading || isLoading;
  const userCredit = fetchData?.localAvailableCredit;

  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [currentRef.current]);

  useEffect(() => {
    if (orderId) {
      setIsLoading(true);
      (async () => {
        await getAdvisorBonusApi(orderId).then((data: any) => {
          const defaultBonus = data?.advisorBonus?.bonusAmounts?.find((bonus: Advisor.Bonus) => bonus.default);
          setSelectedBonus(defaultBonus);
          setFetchData(data);
          if (setAdvisorBonusSkipEventProperties) {
            setAdvisorBonusSkipEventProperties(data?.skipEventProperties);
          }
          setIsLoading(false);
          checkIfCanMakeApplePayPayments().then(() => {
            if (initialLoadedHandler) initialLoadedHandler();
          });
        }).catch((err: any) => {
          trackToBugsnagApiError('Error fetching advisor bonus', err);
        });
      })();
    }
  }, [orderId]);

  const renderAdvisorAvatar = () => (
    <div className={ classes.advisorAvatarContainer }>
      <Avatar
        imgSrc={ fetchData?.advisor?.profilePictureUrl }
        size="large"
        type={ isKA() ? 'rectangle' : 'round' }
      />
    </div>
  );

  const getBalanceKey = () => {
    if (userCredit === 0) {
      return 'advisor_bonus.no_balance';
    }
    const effectiveBonusAmount = selectedBonus?.localAmount;
    return `advisor_bonus.${ userCredit >= effectiveBonusAmount ? 'enough_balance' : 'partial_balance' }`;
  };

  const renderBalanceContainer = () => (
    <div className={ cn(classes.balanceContainer, { [classes.skeleton]: skeletonLoading }) } data-testid="advisor-balance-container">
      <div className={ classes.balanceValue }>
        { t('advisor_bonus.your_balance', { amount: formatLocalCurrency(parseFloat(String(userCredit)), bonuses.currency) }) }
      </div>
      <div className={ classes.balanceText }>
        <Trans
          i18nKey={ getBalanceKey() }
        />
      </div>
    </div>
  );

  const renderBonusList = () => (
    <div className={ cn(classes.bonusListContainer, { [classes.skeleton]: skeletonLoading }) } data-testid="advisor-bonus-list">
      { bonuses.bonusAmounts.map((bonus, index) => (
        <div
          key={ `amount-${ bonus.localAmount }` }
          className={ cn(classes.bonusItem, { [classes.active]: bonus.localAmount === selectedBonus.localAmount }) }
          onClick={ () => setSelectedBonus(bonus) }
          onKeyDown={ () => setSelectedBonus(bonus) }
          data-testid={ `advisor-bonus-item-${ bonus.localAmount  }` }
        >
          <SvgIcon
            id={ bonusIconsIds[index] }
            className={ classes.bonusIcon }
            size={ 75 }
          />
          { isPS() ? (
            <div className={ cn(classes.bonusAmount, [classes.bonusAmountPs]) }>
              <>
                <span className={ classes.bonusAmountCurrency }>{ getCurrencySignForCode(bonuses.currency) }</span>
                <span className={ classes.bonusAmountValue }>
                  { formatLocalCurrencyNumberOnly(bonus.localAmount) }
                </span>
              </>
            </div>
          ) : (
            <div className={ classes.bonusAmount }>
              { formatLocalCurrency(bonus.localAmount, bonuses.currency) }
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderAdvisorTitle = () => (
    <div className={ cn(classes.advisorTitleContainer, { [classes.skeleton]: skeletonLoading }) } data-testid="advisor-bonus-title-container">
      <div className={ classes.advisorTitleText }>
        { t('advisor_bonus.title', { name: advisor?.nickname }) }
      </div>
      <div className={ classes.subtitleText }>
        { t('advisor_bonus.subtitle') }
      </div>
    </div>
  );

  const onCancel = () => {
    if (skipBtnHandler) {
      skipBtnHandler();
    }
  };

  const onSkipClick = () => {
    let analyticProperties = {};
    if (fetchData?.skipEventProperties) {
      const { name, properties } = fetchData.skipEventProperties;
      analyticProperties = { [name]: { ...properties, 'processing payment': user?.paymentProcessing, 'bonus click source': clickSource } };
    }
    if (forMobileSendBonusBtnOnClickHandler) {
      skipBtnHandler({ analytic_events: analyticProperties });
    } else {
      parseAndTrackAnalyticEvents(analyticProperties);
      onCancel();
    }
  };

  const [bonusSentPopoverShow, setBonusSentPopoverShow] = useState(false);

  const bonusSentPopoverOnSuccessClick = () => {
    setBonusSentPopoverShow(false);
    onCancel();
  };

  const renderBonusSentPopover = () => (
    <Popover
      isOpen={ bonusSentPopoverShow }
      renderTitle={ () => <></> }
      key="bonus-sent-popover"
      centered
      style={ popoverStyles }
    >
      <div className={ classes.bonusSentPopoverContainer }>
        <div className={ classes.bonusSentPopoverIconContainer }>
          <SvgIcon
            id="advisor_bonus_sent_icon"
            className={ classes.bonusSentPopoverIcon }
            size={ 40 }
          />
        </div>
        <div className={ classes.bonusSentTextContainer }>
          <div className={ classes.bonusSentTitle }>
            {t('advisor_bonus.bonus_sent')}
          </div>
          <div className={ classes.bonusSentText }>
            <Trans
              i18nKey="advisor_bonus.your_bonus_is_on_its_way"
            />
          </div>
        </div>
        <div className={ classes.bonusSentPopoverOkButtonContainer }>
          <button
            type="button"
            className={ classes.bonusSentPopoverOkButton }
            onClick={ bonusSentPopoverOnSuccessClick }
          >
            { t('general.ok') }
          </button>
        </div>
      </div>
    </Popover>
  );

  const [bonusSentPopoverErroShow, setBonusSentPopoverErrorShow] = useState<{ error?: string, description?: string }>({});

  const bonusSentPopoverErrorOnOkClick = () => {
    setBonusSentPopoverErrorShow({});
    onCancel();
  };

  const renderBonusSentPopoverError = () => (
    <Popover
      isOpen={ Object.keys(bonusSentPopoverErroShow).length > 0 }
      renderTitle={ () => <></> }
      key="bonus-sent-popover-error"
      centered
      style={ {
        ...popoverStyles,
        backgroundColor: 'var(--app-background-secondary-color)'
      } }
    >
      <div className={ classes.bonusSentPopoverErrorContainer }>
        <div className={ classes.bonusSentPopoverErrorIconContainer }>
          <SvgIcon
            id="advisor_bonus_error_icon"
            className={ classes.bonusSentPopoverErrorIcon }
          />
        </div>
        <div className={ classes.bonusSentTextContainer }>
          <div className={ classes.bonusSentTitle }>
            { bonusSentPopoverErroShow?.error }
          </div>
          <div className={ classes.bonusSentText }>
            { bonusSentPopoverErroShow?.description }
          </div>
        </div>
        <div className={ classes.bonusSentPopoverOkButtonContainer }>
          <button
            type="button"
            className={ classes.bonusSentPopoverOkButton }
            onClick={ bonusSentPopoverErrorOnOkClick }
          >
            { t('general.ok') }
          </button>
        </div>
      </div>
    </Popover>
  );

  const forMobileBuyBtnOnClick = (requiredCredit?: number) => {
    if (!forMobileSendBonusBtnOnClickHandler) return;
    setPurchaseButtonProcessing(true);

    let paymentAmountParams = {};
    if (requiredCredit) {
      const currentPaymentOption = fetchData.currentPaymentOption[0];

      const { purchaseDetails } = (window as any).store.getState();
      paymentAmountParams = {
        payment_amount: {
          amount: requiredCredit,
          kind: currentPaymentOption.kind,
          id: currentPaymentOption.id,
          gatewayName: currentPaymentOption.gatewayName,
          context: currentPaymentOption.context,
          purchaseFees: purchaseDetails?.fees?.purchaseFees
        }
      };
    }

    const params: ParamsProps = {
      bonus_amount: {
        ...selectedBonus
      },
      ...paymentAmountParams
    };

    forMobileSendBonusBtnOnClickHandler(params);
    setPurchaseButtonProcessing(false);
  };

  const getforMobileBuyBtnOnClick = () => {
    if (forMobileSendBonusBtnOnClickHandler) {
      return forMobileBuyBtnOnClick;
    }
    return null;
  };

  const onSubmitAction = () => {
    setPurchaseButtonProcessing(true);
    if (forMobileSendBonusBtnOnClickHandler) {
      forMobileBuyBtnOnClick();
      setPurchaseButtonProcessing(false);
      return;
    }

    if (clickSource) {
      window.store.dispatch(addParamsToAnalitycs({
        [ANALYTICS_EVENTS.CUSTOMER_SENT_A_BONUS]: { 'bonus click source': clickSource }
      }));
    }

    const params = {
      bonus: {
        price: selectedBonus.amount,
        kind: selectedBonus.kind
      }
    };

    advisorTipsBonusesSubmitApi({ orderId, params }).then(() => {
      setBonusSentPopoverShow(true);
      setPurchaseButtonProcessing(false);
    }).catch((error: any) => {
      error.json().then((jsonError: object) => {
        setBonusSentPopoverErrorShow(jsonError);
        setPurchaseButtonProcessing(false);
      });
    });
  };

  const getPurchaseButtonTitle = () => {
    const effectiveBonusAmount = selectedBonus?.localAmount;
    if (userCredit >= effectiveBonusAmount) {
      return t('advisor_bonus.send_bonus');
    }
    switch (fetchData?.currentPaymentOption[0]?.kind) {
      case 'cc':
      case 'am':
        return t('advisor_bonus.send_bonus');
      case 'pp':
        return (
          <>
            <PurchaseSvgIcon
              id="purchase_button_paypal_icon"
              className={ classes.advisorBonusSourceImg }
            />
            <span className={ classes.advisorBonusSourceTextPayPal }>{ t('advisor_bonus.continue_with_pay_pal') }</span>
          </>
        );
      case 'ap':
        return (
          <>
            <span className={ classes.advisorBonusSourceTextApplePay }>
              { t('advisor_bonus.pay_with_apple_pay') }
            </span>
            <PurchaseSvgIcon
              id="purchase_button_apple_pay_icon"
              className={ classes.advisorBonusSourceImg }
            />
          </>
        );
      case 'gp':
        return (
          <>
            <PurchaseSvgIcon
              id="purchase_button_google_pay_icon"
              className={ classes.advisorBonusSourceImgGP }
            />
            <span className={ classes.advisorBonusSourceTextGooglePay }>{ t('advisor_bonus.continue_with_google_pay') }</span>
          </>
        );
      default:
        return '';
    }
  };

  const getPurchaseButtonTitleClassName = () => {
    const effectiveBonusAmount = selectedBonus?.localAmount;
    if (userCredit >= effectiveBonusAmount) return classes.forMobileBuyBtnContainer_cc;
    const className = classes[`forMobileBuyBtnContainer_${ fetchData?.currentPaymentOption[0]?.kind }` as keyof typeof classes] || classes.forMobileBuyBtnContainer_cc;
    return className;
  };

  const renderFooter = () => (
    <div className={ classes.footerContainer }>
      <PurchaseButtonWrapper
        key={ selectedBonus?.amount }
        purchaseAmount={ selectedBonus?.amount }
        advisorId={ fetchData?.advisor?.id }
        onComplete={ onSubmitAction }
        onError={ onPaymentError }
        showErrorScreen
        onAnalyticsEvent={ (...args) => analyticsService.trackEvent(...args) }
        renderIcon={ renderIcon }
        renderText={ renderText }
        forMobileBuyBtnOnClick={ getforMobileBuyBtnOnClick() }
        forMobileAppClickSource="bonus drawer"
        purchaseType="advisor_bonus"
        clickSource={ clickSource }
        disabled={ skeletonLoading || purchaseButtonProcessing }
        className={ getPurchaseButtonTitleClassName() }
      >
        {getPurchaseButtonTitle()}
      </PurchaseButtonWrapper>
      { !hideSkipButton && (
        <div className={ classes.skipButton } data-testid="advisor-bonus-skip-button">
          <Button
            type="link"
            title={ t('advisor_bonus.skip') }
            onClick={ onSkipClick }
          />
        </div>
      )}
      <div className={ classes.footer } data-testid="advisor-bonus-footer">
        { t('advisor_bonus.footer_text') }
      </div>
    </div>
  );

  const renderAdvisorBonusContainer = () => (
    <div className={ cn(classes.container, classNames) } data-testid="advisor-bonus-container" ref={ currentRef }>
      <div className={ classes.bonusInfoContainer }>
        <div className={ classes.bonusContent }>
          { renderAdvisorAvatar() }
          { renderAdvisorTitle() }
          { renderBonusList() }
          { renderBalanceContainer() }
          { bonusSentPopoverShow && renderBonusSentPopover() }
          { Object.keys(bonusSentPopoverErroShow).length > 0 && renderBonusSentPopoverError() }
        </div>
      </div>
      { renderFooter() }
    </div>
  );

  return renderAdvisorBonusContainer();
};

export default withPGTranslation(AdvisorBonus);
